(function () {
  if (customElements.get('support-chat')) return;

  class SupportChat extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bound === 'true') return;
      this.dataset.bound = 'true';

      this.apiBase = this.normalizeApiBase(this.dataset.apiBase || '');
      this.sessionKey = this.dataset.sessionKey || 'alaska_support_chat_session_id';
      this.messagesKey = this.dataset.messagesKey || 'alaska_support_chat_messages';
      this.openKey = this.dataset.openKey || 'alaska_support_chat_open';
      this.typingLabel = this.dataset.typingLabel || 'A escrever...';
      this.fallbackMessage = this.dataset.fallbackMessage || 'Nao foi possivel responder agora. Tenta novamente dentro de instantes.';
      this.networkMessage = this.dataset.networkMessage || 'Nao conseguimos enviar a mensagem. Verifica a ligacao e tenta novamente.';
      this.rateLimitMessage = this.dataset.rateLimitMessage || 'Recebemos muitas mensagens seguidas. Espera um momento e tenta novamente.';

      this.launcher = this.querySelector('.support-chat__launcher');
      this.panel = this.querySelector('.support-chat__panel');
      this.closeButton = this.querySelector('.support-chat__close');
      this.form = this.querySelector('.support-chat__form');
      this.input = this.querySelector('.support-chat__input');
      this.messagesEl = this.querySelector('.support-chat__messages');
      this.sendButton = this.querySelector('.support-chat__send');

      this.loading = false;
      this.messages = this.loadMessages();
      this.stickyAtc = document.getElementById('CustomStickyATC');
      this.stickyAtcObserver = null;

      if (!this.messages.length) {
        this.messages.push({
          id: this.makeLocalId(),
          role: 'assistant',
          content: this.dataset.welcomeMessage || 'Ola! Sou o assistente da Alaska. Como posso ajudar?',
          createdAt: new Date().toISOString(),
        });
      }

      this.renderMessages();
      this.bindEvents();
      this.bindStickyAtcOffset();

      if (this.readStorage(this.openKey) === 'true') {
        this.open({ focus: false });
      }
    }

    bindEvents() {
      this.launcher?.addEventListener('click', () => this.open());
      this.closeButton?.addEventListener('click', () => this.close());

      this.form?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.submitMessage();
      });

      this.input?.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault();
          this.submitMessage();
        }
      });

      this.input?.addEventListener('input', () => this.resizeInput());

      this.messagesEl?.addEventListener('wheel', (event) => event.stopPropagation(), { passive: true });
      this.messagesEl?.addEventListener('touchmove', (event) => event.stopPropagation(), { passive: true });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.classList.contains('is-open')) {
          this.close();
        }
      });
    }

    bindStickyAtcOffset() {
      this.updateStickyAtcOffset();

      window.addEventListener('scroll', () => this.updateStickyAtcOffset(), { passive: true });
      window.addEventListener('resize', () => this.updateStickyAtcOffset(), { passive: true });
      window.addEventListener('alaska:sticky-atc-visibility-change', () => this.updateStickyAtcOffset(), { passive: true });

      if (!this.stickyAtc || typeof MutationObserver === 'undefined') return;

      this.stickyAtcObserver = new MutationObserver(() => this.updateStickyAtcOffset());
      this.stickyAtcObserver.observe(this.stickyAtc, {
        attributes: true,
        attributeFilter: ['class', 'style'],
      });
    }

    updateStickyAtcOffset() {
      if (!this.stickyAtc) {
        this.stickyAtc = document.getElementById('CustomStickyATC');
      }

      if (
        !this.stickyAtc ||
        !this.stickyAtc.classList.contains('is-visible') ||
        !document.body.classList.contains('sticky-atc-visible')
      ) {
        this.style.setProperty('--support-chat-sticky-atc-offset', '0px');
        return;
      }

      const stickyStyles = window.getComputedStyle(this.stickyAtc);
      const isFixedOverlay =
        stickyStyles.position === 'fixed' &&
        stickyStyles.visibility !== 'hidden' &&
        stickyStyles.display !== 'none' &&
        stickyStyles.pointerEvents !== 'none';

      if (!isFixedOverlay) {
        this.style.setProperty('--support-chat-sticky-atc-offset', '0px');
        return;
      }

      const rect = this.stickyAtc.getBoundingClientRect();
      const intersectsViewport = rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight;

      if (!intersectsViewport) {
        this.style.setProperty('--support-chat-sticky-atc-offset', '0px');
        return;
      }

      const desiredBottom = Math.max(0, window.innerHeight - rect.top + 12);
      const normalBottom = this.normalBottomOffset();
      const extraOffset = Math.max(0, desiredBottom - normalBottom);
      this.style.setProperty('--support-chat-sticky-atc-offset', `${Math.ceil(extraOffset)}px`);
    }

    normalBottomOffset() {
      const styles = window.getComputedStyle(this);
      const property = window.matchMedia('(max-width: 749px)').matches
        ? '--support-chat-bottom-mobile'
        : '--support-chat-bottom-desktop';
      const value = parseFloat(styles.getPropertyValue(property));
      return Number.isFinite(value) ? value : 0;
    }

    open(options = {}) {
      this.classList.add('is-open');
      this.panel.hidden = false;
      this.launcher?.setAttribute('aria-expanded', 'true');
      document.documentElement.classList.add('support-chat-open');
      this.writeStorage(this.openKey, 'true');
      this.scrollToBottom();

      if (options.focus !== false) {
        window.setTimeout(() => this.input?.focus(), 120);
      }
    }

    close() {
      this.classList.remove('is-open');
      this.launcher?.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('support-chat-open');
      this.writeStorage(this.openKey, 'false');

      window.setTimeout(() => {
        if (!this.classList.contains('is-open')) {
          this.panel.hidden = true;
        }
      }, 260);
    }

    async submitMessage() {
      if (this.loading || !this.input) return;

      const content = this.input.value.trim();
      if (!content) return;

      this.input.value = '';
      this.resizeInput();
      this.addMessage({ role: 'user', content });
      this.setLoading(true);

      try {
        if (!this.apiBase) {
          throw new Error('missing_api_base');
        }

        const sessionId = await this.ensureSession();
        const response = await this.postMessage(sessionId, content);
        const replyContent = response?.reply?.content || response?.message || this.fallbackMessage;

        this.addMessage({
          role: 'assistant',
          content: replyContent,
          escalated: Boolean(response?.escalated || response?.status === 'needs_human'),
        });
      } catch (error) {
        this.addMessage({
          role: 'system',
          content: this.messageForError(error),
          error: true,
        });
      } finally {
        this.setLoading(false);
      }
    }

    async ensureSession() {
      const existingSessionId = this.readStorage(this.sessionKey);
      if (existingSessionId) return existingSessionId;

      const response = await this.fetchJson(`${this.apiBase}/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(this.pagePayload({ sessionId: null })),
      });

      const sessionId = response?.sessionId;
      if (!sessionId) {
        throw new Error('missing_session_id');
      }

      this.writeStorage(this.sessionKey, sessionId);
      return sessionId;
    }

    async postMessage(sessionId, message) {
      return this.fetchJson(`${this.apiBase}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(this.pagePayload({ sessionId, message })),
      });
    }

    async fetchJson(url, options) {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 60000);

      try {
        const response = await fetch(url, {
          ...options,
          signal: controller.signal,
        });

        if (!response.ok) {
          const error = new Error(`request_failed_${response.status}`);
          error.status = response.status;
          throw error;
        }

        return response.json();
      } finally {
        window.clearTimeout(timeout);
      }
    }

    pagePayload(extra) {
      return {
        ...extra,
        pageUrl: window.location.href,
        pageTitle: document.title,
        locale: this.dataset.locale || document.documentElement.lang || navigator.language || 'pt-PT',
        shopName: this.dataset.shopName || '',
      };
    }

    addMessage(message) {
      this.messages.push({
        id: message.id || this.makeLocalId(),
        role: message.role,
        content: message.content,
        error: Boolean(message.error),
        escalated: Boolean(message.escalated),
        createdAt: message.createdAt || new Date().toISOString(),
      });

      this.messages = this.messages.slice(-50);
      this.saveMessages();
      this.renderMessages();
    }

    renderMessages() {
      if (!this.messagesEl) return;

      this.messagesEl.innerHTML = '';

      this.messages.forEach((message) => {
        const item = document.createElement('div');
        item.className = [
          'support-chat__message',
          `support-chat__message--${message.role}`,
          message.error ? 'support-chat__message--error' : '',
          message.escalated ? 'support-chat__message--escalated' : '',
        ].filter(Boolean).join(' ');
        this.renderMessageContent(item, message.content);
        this.messagesEl.appendChild(item);
      });

      if (this.loading) {
        this.messagesEl.appendChild(this.createTypingIndicator());
      }

      this.scrollToBottom();
    }

    createTypingIndicator() {
      const typing = document.createElement('div');
      typing.className = 'support-chat__typing';

      const label = document.createElement('span');
      label.textContent = this.typingLabel;

      const dots = document.createElement('span');
      dots.className = 'support-chat__typing-dots';
      dots.setAttribute('aria-hidden', 'true');
      dots.innerHTML = '<span></span><span></span><span></span>';

      typing.append(label, dots);
      return typing;
    }

    renderMessageContent(element, content) {
      const text = String(content || '');
      const urlPattern = /https?:\/\/[^\s<>"']+/g;
      let cursor = 0;
      let match;

      while ((match = urlPattern.exec(text)) !== null) {
        if (match.index > cursor) {
          element.appendChild(document.createTextNode(text.slice(cursor, match.index)));
        }

        const href = match[0].replace(/[),.;!?]+$/, '');
        const trailing = match[0].slice(href.length);
        const link = document.createElement('a');
        link.href = href;
        link.textContent = href;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        element.appendChild(link);

        if (trailing) {
          element.appendChild(document.createTextNode(trailing));
        }

        cursor = match.index + match[0].length;
      }

      if (cursor < text.length) {
        element.appendChild(document.createTextNode(text.slice(cursor)));
      }
    }

    setLoading(isLoading) {
      this.loading = isLoading;
      this.classList.toggle('is-loading', isLoading);
      if (this.sendButton) this.sendButton.disabled = isLoading;
      if (this.input) this.input.disabled = isLoading;
      this.renderMessages();
    }

    resizeInput() {
      if (!this.input) return;
      this.input.style.height = 'auto';
      this.input.style.height = `${Math.min(this.input.scrollHeight, 120)}px`;
    }

    scrollToBottom() {
      window.requestAnimationFrame(() => {
        if (!this.messagesEl) return;
        this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
      });
    }

    loadMessages() {
      try {
        const raw = this.readStorage(this.messagesKey);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed.filter((message) => message && message.content) : [];
      } catch {
        return [];
      }
    }

    saveMessages() {
      this.writeStorage(this.messagesKey, JSON.stringify(this.messages));
    }

    readStorage(key) {
      try {
        return window.localStorage.getItem(key);
      } catch {
        return null;
      }
    }

    writeStorage(key, value) {
      try {
        window.localStorage.setItem(key, value);
      } catch {
        // Storage can be unavailable in private browsing modes.
      }
    }

    makeLocalId() {
      if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return `local_${window.crypto.randomUUID()}`;
      }
      return `local_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    }

    normalizeApiBase(value) {
      return value.trim().replace(/\/$/, '');
    }

    messageForError(error) {
      if (error?.status === 429) return this.rateLimitMessage;
      if (error?.name === 'AbortError') return this.networkMessage;
      if (error?.message === 'missing_api_base') return this.fallbackMessage;
      return this.networkMessage || this.fallbackMessage;
    }
  }

  customElements.define('support-chat', SupportChat);
})();
